import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Label, Textarea, useGql } from '@ah/design';
import { BottomSheet } from '@ah/design/mobile';
import { Flag } from 'lucide-react';
import { REPORT_CONTENT_M } from '../shared/queries.ts';
import type { ReportTargetType } from '../shared/types.ts';

const REASONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'spam', label: 'Spam or scam' },
  { value: 'harassment', label: 'Harassment or hate speech' },
  { value: 'sexual', label: 'Sexual or explicit content' },
  { value: 'violence', label: 'Violence or threats' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'other', label: 'Something else' },
];

/**
 * Bottom-sheet for reporting any UGC surface. Reasons are predefined so
 * the queue is bucketable; "Something else" plus the free-text box covers
 * everything else.
 *
 * Apple App Review (Guideline 1.2) requires this to be reachable from
 * any place a user can encounter UGC — chat messages, circle headers,
 * other users' profiles, etc.
 */
export function ReportSheet({
  open,
  onClose,
  targetType,
  targetId,
  title,
  onReported,
}: {
  open: boolean;
  onClose: () => void;
  targetType: ReportTargetType;
  targetId: string;
  title?: string;
  onReported?: () => void;
}) {
  const gql = useGql();
  const [reason, setReason] = useState<string>('');
  const [detail, setDetail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: () => {
      const composed = [reason ? REASONS.find((r) => r.value === reason)?.label : null, detail.trim() || null]
        .filter(Boolean)
        .join(' — ');
      return gql.request(REPORT_CONTENT_M, {
        targetType,
        targetId,
        reason: composed || null,
      });
    },
    onSuccess: () => {
      setSubmitted(true);
      onReported?.();
    },
  });

  function handleClose() {
    setReason('');
    setDetail('');
    setSubmitted(false);
    onClose();
  }

  return (
    <BottomSheet open={open} onClose={handleClose} title={title ?? 'Report'}>
      {submitted ? (
        <div className="px-4 py-6 text-center space-y-2">
          <div className="mx-auto size-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Flag size={20} className="text-primary" />
          </div>
          <p className="text-sm font-medium">Thanks — we've received your report.</p>
          <p className="text-xs text-muted-foreground">
            Our team reviews reports within 24 hours and will take action on
            anything that breaks our community rules.
          </p>
          <Button className="w-full mt-4" onClick={handleClose}>
            Done
          </Button>
        </div>
      ) : (
        <div className="px-4 py-3 space-y-4">
          <p className="text-xs text-muted-foreground">
            Your report is anonymous to the other person. We'll review within
            24 hours.
          </p>
          <div className="space-y-2">
            <Label className="text-xs">Reason</Label>
            <div className="grid grid-cols-1 gap-1.5">
              {REASONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setReason(r.value)}
                  className={`text-left text-sm px-3 py-2 rounded-md border transition-colors ${
                    reason === r.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-accent'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="report-detail" className="text-xs">
              Anything else? (optional)
            </Label>
            <Textarea
              id="report-detail"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              maxLength={500}
              placeholder="A few words to help us act faster…"
              className="min-h-[80px] text-sm"
            />
          </div>
          {mutation.isError && (
            <p className="text-sm text-destructive">
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Failed to submit report — try again.'}
            </p>
          )}
          <Button
            className="w-full"
            disabled={!reason || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Sending…' : 'Submit report'}
          </Button>
        </div>
      )}
    </BottomSheet>
  );
}
